
import { useState } from 'react';

export default function HeroSection() {
  const [wishText, setWishText] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitMessage, setSubmitMessage] = useState('');

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!wishText.trim()) return;

    setIsSubmitting(true);
    
    try {
      const formData = new FormData();
      formData.append('wish', wishText);
      formData.append('timestamp', new Date().toISOString());

      const response = await fetch('https://readdy.ai/api/form/d4478gmpguuav66rgbb0', {
        method: 'POST',
        body: new URLSearchParams(formData as any)
      });

      if (response.ok) {
        setSubmitMessage('许愿成功！您的愿望已经收到，期待惊喜降临！');
        setWishText('');
      } else {
        setSubmitMessage('许愿失败，请稍后重试');
      }
    } catch (error) {
      setSubmitMessage('许愿失败，请稍后重试');
    } finally {
      setIsSubmitting(false);
      setTimeout(() => setSubmitMessage(''), 3000);
    }
  };

  return (
    <section id="home" className="min-h-screen relative overflow-hidden bg-gradient-to-br from-purple-900 via-pink-800 to-indigo-900 pt-20">
      {/* Background Image */}
      <div 
        className="absolute inset-0 opacity-30"
        style={{
          backgroundImage: `url('https://readdy.ai/api/search-image?query=magical%20floating%20colorful%20easter%20eggs%20and%20gift%20boxes%20in%20dreamy%20starry%20night%20sky%2C%20purple%20pink%20gradient%20background%2C%20sparkles%20and%20light%20particles%2C%20fantasy%20atmosphere%2C%20ethereal%20glow%2C%20digital%20art%20style%2C%20high%20quality%204k&width=1920&height=1080&seq=hero-new-bg&orientation=landscape')`,
          backgroundSize: 'cover',
          backgroundPosition: 'center',
          backgroundRepeat: 'no-repeat'
        }}
      />
      
      {/* Animated Background Elements */}
      <div className="absolute inset-0 overflow-hidden">
        <div className="absolute top-20 left-10 w-6 h-6 bg-yellow-400/60 rounded-full animate-bounce" style={{ animationDelay: '0s', animationDuration: '3s' }}></div>
        <div className="absolute top-40 right-20 w-8 h-8 bg-pink-400/60 rounded-full animate-bounce" style={{ animationDelay: '1s', animationDuration: '4s' }}></div>
        <div className="absolute bottom-40 left-20 w-5 h-5 bg-purple-400/60 rounded-full animate-bounce" style={{ animationDelay: '2s', animationDuration: '3.5s' }}></div>
        <div className="absolute bottom-32 right-10 w-4 h-4 bg-orange-400/60 rounded-full animate-bounce" style={{ animationDelay: '0.5s', animationDuration: '2.5s' }}></div>
        <div className="absolute top-1/3 left-1/4 w-3 h-3 bg-cyan-400/60 rounded-full animate-bounce" style={{ animationDelay: '1.5s', animationDuration: '4.5s' }}></div>
        <div className="absolute top-2/3 right-1/3 w-7 h-7 bg-emerald-400/60 rounded-full animate-bounce" style={{ animationDelay: '2.5s', animationDuration: '3.2s' }}></div>
      </div>

      {/* Main Content */}
      <div className="relative z-10 flex items-center justify-center min-h-screen">
        <div className="container mx-auto px-4 py-16">
          <div className="max-w-5xl mx-auto text-center">
            
            {/* Main Title */}
            <div className="mb-8">
              <div className="inline-flex items-center justify-center w-20 h-20 bg-gradient-to-r from-yellow-400 to-orange-400 rounded-full mb-8 shadow-2xl">
                <i className="ri-gift-2-line text-3xl text-white"></i>
              </div>
              
              <h1 className="text-4xl md:text-6xl font-bold text-white mb-6 leading-tight">
                <span className="bg-gradient-to-r from-yellow-300 via-pink-300 to-purple-300 bg-clip-text text-transparent">
                  许愿蛋
                </span>
              </h1>
              
              <p className="text-xl md:text-2xl text-purple-100 mb-4 font-medium">
                彩蛋，福利蛋，惊喜不间断
              </p>
              
              <p className="text-base text-purple-200 max-w-3xl mx-auto leading-relaxed">
                致力于为每一位用户带来充满期待的欢乐时刻，让每一次许愿都变成美好的现实！
              </p>
            </div>

            {/* Interactive Wish Form */}
            <div className="max-w-3xl mx-auto mb-12">
              <div className="bg-white/10 backdrop-blur-xl rounded-3xl p-8 border border-white/20 shadow-2xl">
                <div className="flex items-center justify-center mb-6">
                  <div className="flex items-center space-x-2 text-white">
                    <i className="ri-magic-line text-2xl text-yellow-300"></i>
                    <h3 className="text-xl font-semibold">许下你的心愿</h3>
                    <i className="ri-magic-line text-2xl text-yellow-300"></i>
                  </div>
                </div>
                
                <form onSubmit={handleSubmit} className="space-y-6">
                  <div className="relative">
                    <textarea
                      value={wishText}
                      onChange={(e) => setWishText(e.target.value)}
                      placeholder="在这里写下您最真挚的愿望..."
                      className="w-full px-6 py-4 rounded-2xl bg-white/20 border border-white/30 text-white placeholder-white/70 focus:outline-none focus:ring-2 focus:ring-yellow-400 focus:border-transparent resize-none text-lg"
                      rows={4}
                      maxLength={500}
                      required
                    />
                    <div className="absolute bottom-3 right-3 text-sm text-white/60">
                      {wishText.length}/500
                    </div>
                  </div>
                  
                  <button 
                    type="submit" 
                    disabled={isSubmitting || !wishText.trim()}
                    className="w-full bg-gradient-to-r from-yellow-500 via-orange-500 to-red-500 text-white px-8 py-4 rounded-2xl font-bold text-lg hover:shadow-2xl hover:scale-105 transition-all duration-300 cursor-pointer whitespace-nowrap disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {isSubmitting ? (
                      <>
                        <i className="ri-loader-4-line animate-spin mr-2"></i>
                        许愿中...
                      </>
                    ) : (
                      <>
                        <i className="ri-star-smile-line mr-2"></i>
                        立即许愿
                      </>
                    )}
                  </button>
                  
                  {submitMessage && (
                    <div className={`p-4 rounded-xl text-center font-medium ${
                      submitMessage.includes('成功') 
                        ? 'bg-green-500/20 text-green-100 border border-green-400/30' 
                        : 'bg-red-500/20 text-red-100 border border-red-400/30'
                    }`}>
                      {submitMessage}
                    </div>
                  )}
                </form>
              </div>
            </div>

            {/* Action Buttons */}
            <div className="flex flex-col sm:flex-row gap-6 justify-center items-center mb-12">
              <button className="group bg-gradient-to-r from-purple-600 to-pink-600 text-white px-10 py-4 rounded-full font-bold text-lg hover:shadow-2xl hover:scale-105 transition-all duration-300 cursor-pointer whitespace-nowrap">
                <i className="ri-treasure-map-line mr-3 group-hover:animate-bounce"></i>
                探索彩蛋活动
              </button>
              <button className="group border-2 border-white/50 text-white px-10 py-4 rounded-full font-bold text-lg hover:bg-white hover:text-purple-600 hover:shadow-2xl hover:scale-105 transition-all duration-300 cursor-pointer whitespace-nowrap backdrop-blur-sm">
                <i className="ri-gift-line mr-3 group-hover:animate-bounce"></i>
                福利中心
              </button>
            </div>

            {/* Statistics Cards */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-6 mb-16">
              <div className="bg-white/10 backdrop-blur-xl rounded-2xl p-6 border border-white/20 hover:bg-white/20 transition-all duration-300 group">
                <div className="text-4xl md:text-5xl font-bold text-yellow-300 mb-2 group-hover:scale-110 transition-transform duration-300">10万+</div>
                <div className="text-purple-100 font-medium">许愿用户</div>
                <i className="ri-user-heart-line text-2xl text-purple-300 mt-2"></i>
              </div>
              <div className="bg-white/10 backdrop-blur-xl rounded-2xl p-6 border border-white/20 hover:bg-white/20 transition-all duration-300 group">
                <div className="text-4xl md:text-5xl font-bold text-green-300 mb-2 group-hover:scale-110 transition-transform duration-300">50万+</div>
                <div className="text-purple-100 font-medium">愿望实现</div>
                <i className="ri-check-double-line text-2xl text-green-300 mt-2"></i>
              </div>
              <div className="bg-white/10 backdrop-blur-xl rounded-2xl p-6 border border-white/20 hover:bg-white/20 transition-all duration-300 group">
                <div className="text-4xl md:text-5xl font-bold text-orange-300 mb-2 group-hover:scale-110 transition-transform duration-300">100+</div>
                <div className="text-purple-100 font-medium">彩蛋活动</div>
                <i className="ri-gift-2-line text-2xl text-orange-300 mt-2"></i>
              </div>
              <div className="bg-white/10 backdrop-blur-xl rounded-2xl p-6 border border-white/20 hover:bg-white/20 transition-all duration-300 group">
                <div className="text-4xl md:text-5xl font-bold text-pink-300 mb-2 group-hover:scale-110 transition-transform duration-300">365天</div>
                <div className="text-purple-100 font-medium">惊喜不断</div>
                <i className="ri-calendar-event-line text-2xl text-pink-300 mt-2"></i>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Scroll Indicator */}
      <div className="absolute bottom-8 left-1/2 transform -translate-x-1/2 text-white animate-bounce z-20">
        <div className="flex flex-col items-center space-y-2">
          <span className="text-sm text-purple-200">向下滚动探索更多</span>
          <i className="ri-arrow-down-line text-2xl"></i>
        </div>
      </div>
    </section>
  );
}
